import React from 'react';

/**
 * DUPLICATE de SkeletonScreen.jsx
 * Utilisé pour le chargement du CONTENU des pages lors de la navigation.
 * - Même design shimmer
 * - Variante par page (dashboard / form / table / page)
 * - Peut être forcé à durer (ex: 2s) via le parent
 */

const SkeletonElement = ({ className = '', width = '100%', height = '1rem' }) => {
  return (
    <div
      className={`bg-gray-200 rounded-lg ${className}`}
      style={{
        width,
        height,
        background: 'linear-gradient(90deg, #f0f0f0 0%, #e0e0e0 20%, #f0f0f0 40%, #f0f0f0 100%)',
        backgroundSize: '1000px 100%',
        animation: 'shimmer 2s infinite'
      }}
    />
  );
};

const SkeletonHeader = () => (
  <div className="bg-white border-b border-gray-200 w-full">
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex items-center justify-between">
        <div className="space-y-3 flex-1">
          <SkeletonElement height="2rem" width="40%" className="max-w-xs" />
          <SkeletonElement height="1rem" width="60%" className="max-w-md" />
        </div>
        <SkeletonElement height="3rem" width="8rem" className="rounded-2xl" />
      </div>
    </div>
  </div>
);

const SkeletonCard = ({ variant = 'default' }) => {
  if (variant === 'stat') {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-3">
          <SkeletonElement height="2.5rem" width="2.5rem" className="rounded-xl" />
          <SkeletonElement height="1.25rem" width="40%" />
        </div>
        <SkeletonElement height="2.5rem" width="60%" />
        <SkeletonElement height="0.875rem" width="50%" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <SkeletonElement height="1.5rem" width="30%" />
        <SkeletonElement height="2rem" width="6rem" className="rounded-xl" />
      </div>
      <div className="space-y-3">
        <SkeletonElement height="1rem" width="100%" />
        <SkeletonElement height="1rem" width="90%" />
        <SkeletonElement height="1rem" width="85%" />
      </div>
      <div className="flex gap-2 pt-2">
        <SkeletonElement height="2.5rem" width="8rem" className="rounded-xl" />
        <SkeletonElement height="2.5rem" width="8rem" className="rounded-xl" />
      </div>
    </div>
  );
};

const SkeletonTable = () => (
  <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
    <div className="border-b border-gray-200 p-4">
      <div className="flex items-center justify-between">
        <SkeletonElement height="1.5rem" width="25%" />
        <SkeletonElement height="2rem" width="8rem" className="rounded-xl" />
      </div>
    </div>
    <div className="divide-y divide-gray-100">
      {[...Array(5)].map((_, i) => (
        <div key={i} className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4 flex-1">
            <SkeletonElement height="2.5rem" width="2.5rem" className="rounded-full" />
            <div className="space-y-2 flex-1">
              <SkeletonElement height="1rem" width="40%" />
              <SkeletonElement height="0.875rem" width="60%" />
            </div>
          </div>
          <div className="flex gap-2">
            <SkeletonElement height="2rem" width="2rem" className="rounded-lg" />
            <SkeletonElement height="2rem" width="2rem" className="rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const SkeletonForm = () => (
  <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-6">
    <div className="space-y-2 pb-4 border-b border-gray-100">
      <SkeletonElement height="1.75rem" width="35%" />
      <SkeletonElement height="1rem" width="50%" />
    </div>
    <div className="space-y-5">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="space-y-2">
          <SkeletonElement height="0.875rem" width="25%" />
          <SkeletonElement height="3rem" width="100%" className="rounded-xl" />
        </div>
      ))}
    </div>
    <div className="flex gap-3 pt-4">
      <SkeletonElement height="3rem" width="10rem" className="rounded-xl" />
      <SkeletonElement height="3rem" width="10rem" className="rounded-xl" />
    </div>
  </div>
);

const PageContentSkeleton = ({ children, isLoading, variant = 'page' }) => {
  if (!isLoading) return children;

  const renderSkeleton = () => {
    switch (variant) {
      case 'dashboard':
        return (
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <SkeletonHeader />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <SkeletonCard key={i} variant="stat" />
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SkeletonCard />
              <SkeletonCard />
            </div>
            <SkeletonTable />
          </div>
        );

      case 'form':
        return (
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <SkeletonHeader />
            <div className="max-w-3xl mx-auto mt-6">
              <SkeletonForm />
            </div>
          </div>
        );

      case 'table':
        return (
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
            <SkeletonHeader />
            <div className="mt-6">
              <SkeletonTable />
            </div>
          </div>
        );

      case 'page':
      default:
        return (
          <div className="w-full px-4 sm:px-6 lg:px-8 py-6 space-y-6">
            <SkeletonHeader />
            <div className="space-y-6">
              <SkeletonCard />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <SkeletonCard />
                <SkeletonCard />
              </div>
              <SkeletonTable />
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div className="w-full">{renderSkeleton()}</div>
      <style>{`
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
      `}</style>
    </>
  );
};

export default PageContentSkeleton;

